import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withNavigationErrorHandler } from '@angular/router';
import { reloadOnStaleChunk } from './core/chunk-load-recovery';
import { NavigationHistoryService } from './core/services/navigation-history.service';
import { FilterService } from 'primeng/api';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { LucideAngularModule, icons } from 'lucide-angular';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { AuthService } from './core/services/auth.service';

/** Rótulos do PrimeNG em português. Cobre calendário, tabela, paginação e filtros. */
const PT_BR = {
  dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
  dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
  monthNames: [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ],
  monthNamesShort: [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
  ],
  today: 'Hoje',
  clear: 'Limpar',
  weekHeader: 'Sem',
  firstDayOfWeek: 0,
  dateFormat: 'dd/mm/yy',
  chooseDate: 'Escolher data',
  prevDecade: 'Década anterior',
  nextDecade: 'Próxima década',
  prevYear: 'Ano anterior',
  nextYear: 'Próximo ano',
  prevMonth: 'Mês anterior',
  nextMonth: 'Próximo mês',
  prevHour: 'Hora anterior',
  nextHour: 'Próxima hora',
  prevMinute: 'Minuto anterior',
  nextMinute: 'Próximo minuto',
  chooseYear: 'Escolher ano',
  chooseMonth: 'Escolher mês',
  accept: 'Sim',
  reject: 'Não',
  emptyMessage: 'Nenhum resultado encontrado',
  emptyFilterMessage: 'Nenhum resultado encontrado',
  emptySearchMessage: 'Nenhum resultado encontrado',
  emptySelectionMessage: 'Nenhum item selecionado',
  searchMessage: '{0} resultados disponíveis',
  selectionMessage: '{0} itens selecionados',
  removeLabel: 'Remover',
  clearFilter: 'Limpar filtro',
  matchAll: 'Todos',
  matchAny: 'Qualquer',
  addRule: 'Adicionar regra',
  removeRule: 'Remover regra',
  apply: 'Aplicar',
  cancel: 'Cancelar',
  pending: 'Pendente',
  fileChosenMessage: '{0} arquivos',
  noFileChosenMessage: 'Nenhum arquivo escolhido',
};

export const livThemePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fbfefb',
      100: '#eef2ed',
      200: '#dee3dc',
      300: '#cad0c8',
      400: '#737f70',
      500: '#536250',
      600: '#455442',
      700: '#374635',
      800: '#263424',
      900: '#1e2b1c',
      950: '#172414',
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            success: {
              background: '{primary.color}',
              hoverBackground: '{primary.hover.color}',
              activeBackground: '{primary.active.color}',
              borderColor: '{primary.color}',
              hoverBorderColor: '{primary.hover.color}',
              activeBorderColor: '{primary.active.color}',
              color: '{primary.contrast.color}',
              hoverColor: '{primary.contrast.color}',
              activeColor: '{primary.contrast.color}',
              focusRing: {
                color: '{primary.color}',
                shadow: 'none',
              },
            },
          },
          outlined: {
            success: {
              hoverBackground: '{primary.50}',
              activeBackground: '{primary.100}',
              borderColor: '{primary.200}',
              color: '{primary.color}',
            },
          },
          text: {
            success: {
              hoverBackground: '{primary.50}',
              activeBackground: '{primary.100}',
              color: '{primary.color}',
            },
          },
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    // `registerLocaleData` em main.ts só CARREGA o pt-BR; sem este provider o Angular
    // segue em en-US e os pipes de moeda imprimem "R$4,533" — que um brasileiro lê como
    // quatro reais. Num portal que vende apartamento isso muda o sentido do número.
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withNavigationErrorHandler(reloadOnStaleChunk)),
    // O histórico de navegação precisa existir DESDE O INÍCIO.
    //
    // Sendo `providedIn: 'root'`, ele só nascia quando alguém o injetava — ou seja, já dentro
    // da tela de detalhe, depois da navegação que ele deveria ter contado. Resultado: ele achava
    // que não havia página anterior e o "Voltar" caía no destino fixo, que é o defeito que ele
    // existe para corrigir.
    provideAppInitializer(() => {
      inject(NavigationHistoryService);
    }),
    // Modo de filtro para selects cuja busca acontece no servidor: o PrimeNG filtraria de
    // novo o que a API já filtrou e esconderia resultados. Registrado no arranque porque
    // vários formulários usam isso sem passar pelo editor de tabela.
    provideAppInitializer(() => {
      inject(FilterService).register('livRemoteResults', () => true);
    }),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideAppInitializer(() => inject(AuthService).restoreSession()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: livThemePreset,
        options: {
          darkModeSelector: false,
          cssLayer: false,
        },
      },
      // O LOCALE_ID acima só alcança os pipes do Angular. Os componentes do PrimeNG carregam
      // os próprios rótulos, e sem isto o calendário abria em inglês — "September", "Su Mo Tu" —
      // dentro de um portal inteiro em português.
      translation: PT_BR,
    }),
    ...(LucideAngularModule.pick(icons).providers || []),
  ],
};
